import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormField, FormGrid, HpButton, HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { PdLink } from '../../components/pd-link';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { invalidateSelfservice, type GeneratedLink } from './queries';

function parseIds(input: string): { ids: number[]; invalid: string[] } {
  const tokens = input
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const ids: number[] = [];
  const invalid: string[] = [];
  const seen = new Set<number>();
  for (const token of tokens) {
    if (/^\d+$/.test(token)) {
      const n = Number(token);
      if (n > 0 && !seen.has(n)) {
        seen.add(n);
        ids.push(n);
      }
    } else {
      invalid.push(token);
    }
  }
  return { ids, invalid };
}

function csvEscape(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replaceAll('"', '""')}"` : v;
}

function downloadCsv(links: GeneratedLink[]) {
  const rows = [
    'name,pd_person_id,url',
    ...links.map((l) => `${csvEscape(l.name ?? '')},${l.pdPersonId},${csvEscape(l.url)}`),
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'contact-update-links.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation(['contactUpdates', 'common']);
  const [copied, setCopied] = useState(false);
  return (
    <HpButton
      className="bo-button--sm"
      icon={copied ? 'check' : 'copy'}
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? t('generate.copied') : t('common:actions.copy')}
    </HpButton>
  );
}

// --- Generate links (admin) ---

export function GenerateLinksPanel({ pdDomain }: { pdDomain: string | null | undefined }) {
  const { t } = useTranslation(['contactUpdates', 'common']);
  const [idsInput, setIdsInput] = useState('');
  const [campaign, setCampaign] = useState('');
  const [expiresDays, setExpiresDays] = useState('60');

  const { ids, invalid } = useMemo(() => parseIds(idsInput), [idsInput]);

  const create = useMutation(trpc.selfservice.createLinks.mutationOptions({ onSuccess: invalidateSelfservice }));

  function generate() {
    const body: { pdPersonIds: number[]; campaign?: string; expiresDays?: number } = { pdPersonIds: ids };
    if (campaign.trim()) body.campaign = campaign.trim();
    const days = Number(expiresDays);
    if (Number.isFinite(days) && days > 0) body.expiresDays = Math.floor(days);
    create.mutate(body);
  }

  const links = create.data?.links;

  return (
    <section className="hp-card">
      <HpCardHeader title={t('generate.title')} subtitle={t('generate.subtitle')} icon="link" />
      <div className="hp-card__body bo-sections">
        <FormGrid>
          <FormField label={t('generate.personIds')} hint={t('generate.personIdsHint')} full>
            <textarea
              rows={4}
              className="bo-mono"
              placeholder={'101, 102 103\n104'}
              value={idsInput}
              onChange={(e) => setIdsInput(e.target.value)}
            />
          </FormField>
          <FormField label={t('generate.campaign')}>
            <input type="text" maxLength={100} value={campaign} onChange={(e) => setCampaign(e.target.value)} />
          </FormField>
          <FormField label={t('generate.expiry')}>
            <input
              type="number"
              min={1}
              max={365}
              value={expiresDays}
              onChange={(e) => setExpiresDays(e.target.value)}
            />
          </FormField>
        </FormGrid>

        <div className="bo-row">
          <HpButton
            variant="primary"
            icon="link"
            busy={create.isPending}
            disabled={ids.length === 0 || ids.length > 1000}
            onClick={generate}
          >
            {create.isPending
              ? t('generate.generating')
              : ids.length > 0
                ? t('generate.generate', { count: ids.length })
                : t('generate.generateEmpty')}
          </HpButton>
          <span className="bo-muted bo-mono">{t('generate.validIds', { count: ids.length })}</span>
          {ids.length > 1000 && <HpTag tone="danger">{t('generate.maxBatch')}</HpTag>}
          {invalid.length > 0 && (
            <HpTag tone="warning" title={invalid.join(', ')}>
              {t('generate.invalidTokens', { count: invalid.length })}
            </HpTag>
          )}
        </div>
        <ErrorAlert error={create.error} />
      </div>

      {links && links.length > 0 && (
        <>
          <div className="hp-card__body bo-row bo-links-once">
            <HpTag tone="danger">{t('generate.shownOnce')}</HpTag>
            <HpButton icon="download" onClick={() => downloadCsv(links)}>
              {t('generate.downloadCsv')}
            </HpButton>
          </div>
          <TableScroll label={t('generate.tableLabel')}>
            <BrutTable>
              <BrutTheadRow>
                <th>{t('common:table.name')}</th>
                <th>{t('generate.person')}</th>
                <th>{t('generate.link')}</th>
                <th />
              </BrutTheadRow>
              <tbody>
                {links.map((link) => (
                  <tr key={link.pdPersonId}>
                    <td className="bo-nowrap bo-strong">{link.name ?? '—'}</td>
                    <td className="bo-nowrap">
                      <PdLink domain={pdDomain} entity="person" pdId={link.pdPersonId} />
                    </td>
                    <td className="bo-cell-url">
                      <input
                        readOnly
                        value={link.url}
                        onFocus={(e) => e.currentTarget.select()}
                        className="bo-url-input"
                        aria-label={t('generate.linkAria', { id: link.pdPersonId })}
                      />
                    </td>
                    <td>
                      <CopyButton text={link.url} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </BrutTable>
          </TableScroll>
        </>
      )}
    </section>
  );
}
